<template>
  <div id="app">    
    <localization :language="'de-DE'">
      <intl :locale="'de'">
        <error-popup @closed="errorClosed" v-if="errorDialogParameter.isErrorDialog" v-bind="errorDialogParameter" />
        <information-popup @closed="infoClosed" v-if="infoDialogParameter.isInfoDialog" v-bind="infoDialogParameter" />
        <loader-popup v-if="loaderParameter.isInProgress" v-bind="loaderParameter" />
        <menu-bar v-if="loginParameter.isAuthenticated" />
        
        <login       
          v-if="!loginParameter.isAuthenticated && !errorDialogParameter.isErrorDialog && !loaderParameter.isInProgress"       
          v-bind="loginParameter"
          >
        </login>
        <router-view v-if="loginParameter.isAuthenticated" />
      </intl>
    </localization>
  </div>
</template>

<script lang="ts">

import { onMounted, onUnmounted, reactive, ref } from "vue";
import { ErrorStateServiceForRoot } from "./infrastructure/observables/errorState";
import { InfoStateServiceForRoot } from "./infrastructure/observables/infoState";
import { LoaderStateServiceForRoot } from "./infrastructure/observables/loaderState"
import ErrorPopup from "./components/shared/ErrorPopup.vue";
import InformationPopup from "./components/shared/InformationPopup.vue";
import LoaderPopup from "./components/shared/LoaderPopup.vue";
import Login from "./components/shared/Login.vue"
import MenuBar from "./components/shared/MenuBar.vue";

import { MesageBusServiceForRoot } from "@/infrastructure/observables/messageBus";
import { GridEventService } from "./infrastructure/observables/gridEventState";
import { FormValidation } from "./infrastructure/frontent/validation";
import { Out } from "./infrastructure/frontent/clientMessage";
import { LogoutStateServiceForRoot } from "@/infrastructure/observables/logoutState";

import { IntlProvider, load, loadMessages, LocalizationProvider } from '@progress/kendo-vue-intl';
import deMessages from '@/infrastructure/frontent/languageDe.json'
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';

import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';

import deNumbers from 'cldr-numbers-full/main/de/numbers.json';
import deLocalCurrency from 'cldr-numbers-full/main/de/currencies.json';
import deCaGregorian from 'cldr-dates-full/main/de/ca-gregorian.json';
import deDateFields from 'cldr-dates-full/main/de/dateFields.json';
import { UserProfile } from "./infrastructure/backend/userProfile";
import { Identity } from "@/infrastructure/backend/userIdentity";
import router from "./router";

export default {
  name: "App",
  components: { 
    ErrorPopup, 
    InformationPopup,
    LoaderPopup, 
    Login, 
    MenuBar, 
    'intl': IntlProvider,
    'localization': LocalizationProvider
  },
  inheritAttrs: false,
  
  setup() {

    load(  likelySubtags,currencyData,weekData,deNumbers,deLocalCurrency,deCaGregorian,deDateFields);
    loadMessages(deMessages, 'de-DE');


    const errorState = ErrorStateServiceForRoot.getErrorStateService()
    const errorDialogParameter = reactive({
      isErrorDialog: false,
      endpoint: "",
      dialogName: "",
      title: "",
      text: ""
      })

      const errorStateSubscription = errorState.subscription().subscribe({
      next: (value) => {
        errorDialogParameter.endpoint = value.endpoint;  
        errorDialogParameter.isErrorDialog = value.isError;   
        errorDialogParameter.text = value.userError;
        if ( value.isError) {    
          errorDialogParameter.dialogName = "Fehler";
          errorDialogParameter.title = "Fehler";          
          return;
        } 
          errorDialogParameter.dialogName = "";
          errorDialogParameter.title = "";          
      }
    })

    const infoState = InfoStateServiceForRoot.getInfoStateService()
    const infoDialogParameter = reactive({
      isInfoDialog: false,
      dialogName: "",
      title: "",
      text: ""
      })

      const infoStateSubscription = infoState.subscription().subscribe({
      next: (value) => {
        infoDialogParameter.dialogName = "Information";
        infoDialogParameter.title = value.infoTitle;     
        infoDialogParameter.text = value.infoText;     
        infoDialogParameter.isInfoDialog = value.isInfo
      }
    })


    const loaderState = LoaderStateServiceForRoot.getLoaderStateService()

    const loaderParameter = reactive({
      isInProgress: false
    })

    const loaderStateSubscription = loaderState.subscription().subscribe({
      next: (value) => {
        loaderParameter.isInProgress = value.isInProgress;   
      }
    })

    const messageBus = MesageBusServiceForRoot.getMessageStateService();
    const messageStateSubscription = messageBus.subscription().subscribe({
      next: (value) => {
        if ( value.source === "login" &&  value.topic ==="loginSuccess") {
          loginParameter.isAuthenticated = true     
          const validationRules =  new  FormValidation() 
          if ( !validationRules ) {
            Out.error("App.ValidationRules.Error => No ValidationRules")
          }

        } 
      }
    })

    function errorClosed() {
      errorDialogParameter.isErrorDialog = false;
    }
    function infoClosed() {
      infoDialogParameter.isInfoDialog = false;
    }
    

    const loginParameter = reactive({
      startUri: "api/authentication/start",
      loginUri: "api/authentication/login",
      isAuthenticated: false
    })

    function Logout() {
      loginParameter.isAuthenticated = false;
      UserProfile.ClearUserProfile()
      Identity.clearIdentity()
      //document.location.reload()
    }

    const logoutEvent = LogoutStateServiceForRoot.getLogoutStateService()
    const onLogoutEvent = logoutEvent.subscription().subscribe({
      next: (value) => {
        if ( value.isLogout) {
          Logout()
        }
      }
    })


    function disableBackButton(event: any) {
      history.pushState(null, '', location.href);
      alert('Der Zurück-Button wird nicht unterstützt! Bitte verwenden Sie die Navigationselemente auf der Seite.');
    }

    router.beforeEach((to, from, next) => {
      next(); 
    })

    // router.beforeEach((to, from, next) => {
    //  if (to && from && to.name && from.name &&  to.name === from.name) {
    //   next(false);
    //  }
    //  next();
    // });

    onMounted(() => {
      window.addEventListener('popstate', disableBackButton);
      document.title = "Aidcomm.Web6"
    })


    onUnmounted(() => {
        window.removeEventListener('popstate', disableBackButton);
        errorStateSubscription.unsubscribe();
        loaderStateSubscription.unsubscribe();
        messageStateSubscription.unsubscribe();
        infoStateSubscription.unsubscribe();
        onLogoutEvent.unsubscribe();
      })


    return {
      errorDialogParameter,
      infoDialogParameter,
      loaderParameter,
      loginParameter,
      errorClosed,
      infoClosed,
      Logout
    }
  },
};
</script>
<style lang="scss">

//$kendo-color-primary: #00FF00
//@use "@progress/kendo-theme-bootstrap/scss/all.scss" as *;
@import 'bootstrap/dist/css/bootstrap.css';


#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}

.app__backdrop {
  position: fixed;
  z-index: 100;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
}



.landingpage__container {
    max-width: 100px !important;
    min-width: 800px;
    margin-top: 5%;
    margin-bottom: 5%;
}

.master__headline {
  padding-top: 1vh;
  text-align: center;
  color: rgb(0, 86, 150);
  font-size: 1.9rem;
  font-weight: bold;
  min-height: 16vh;
  height: 16vh;
}

.background__image {  
  width: 100%;
  height: 100%;
  background-size: cover;
  background-origin: border-box;
  background-clip: border-box;
  background-position-y: -7.6vh;
  background-attachment: fixed;
  background-repeat: no-repeat;
  z-index: -1000;
}

.k-floating-label-container.k-empty>.k-floating-label {
    top: var(--kendo-floating-label-offset-y, calc(var(--kendo-font-size, inherit)* 1.5 - 5px + var(--kendo-spacing-1\.5, 0.375rem)))!important;
    left: var(--kendo-floating-label-offset-x, calc(var(--kendo-spacing-3, 0.75rem) + 1px));
    transform: scale(1);
    pointer-events: none;.k-grid tbody td {
    text-overflow: clip; /* prevent the ellipsis behavior */
    overflow-wrap: break-word;
}
}


.k-table-td {
  font-size: 0.75rem!important;
} 

.k-table-th {
  font-size: 0.75rem!important;
  padding-top: 6px!important;
  padding-bottom: 6px!important;
} 


.k-cell-inner {
  font-size: 0.75rem!important;
  font-weight: 350!important;
}


.k-grid {
  font-size: 0.75rem!important;
    background-color:white!important;
}

.k-grid-md {
  font-size: 0.75rem!important;
  background-color:white!important;
}

.k-grid-header {
    background-color: white!important;
}

.k-grid-toolbar {
  padding-top: 0!important;  
  padding-bottom: 0!important;  
  line-height: 0!important;
  font-size: 0.75rem!important;
}

.k-toolbar {
  padding-top: 0!important;  
  padding-bottom: 0!important;  
  line-height: 0!important;
  font-size: 0.75rem!important;
}


.k-input {
    font-size: 0.75!important;
    padding-top: 2px!important;
    padding-bottom: 4px!important;
    padding-left: 4px!important;
    font-weight: bold!important;
    
}

.k-input-inner {
    font-size: 0.75rem!important;
    padding-top: 2px!important;
    padding-bottom: 4px!important;
}

.k-input-validation-icon {
  height: calc( 4px + 0.75rem )!important;
}

.k-combobox {
    font-size: 0.75rem!important;
    height: 26px!important;
    padding-top: 2px!important;
    padding-bottom: 2px!important;
    padding-left: 4px!important;

}

.k-label {
    font-size: 0.75rem!important;
    padding-top: 0px!important;
    padding-bottom: 0px!important;
    margin-top: -4px!important;
    color: grey!important 
}

.k-button-md {
    padding-top: 0px!important;
    padding-bottom: 2px!important;    
}

.k-icon-button {
  margin: 0px!important;
  padding-top: 0px!important;
  padding-bottom: 0px!important;
}

.k-button-md.k-icon-button {
    //padding: 0; 
}

.k-dropdown-operator.k-picker-md .k-input-button {
    padding: 0px !important;
}

.k-input-md .k-input-inner, .k-picker-md .k-input-inner {
    padding: 0px!important;
    font-size: 0.75rem!important;
}

.k-input-md, .k-picker-md {
    font-size: 0.75rem!important;
    line-height: 1.5;
}

.k-input-inner  {
  //height: 1.1rem!important;
  margin-top: 1px!important
}

input.k-input-inner {
  padding-top: 2px!important;
  height: 1.2rem!important;
}

.k-checkbox {
  font-size:  0.75!important;
  padding-top: 0px!important;
  padding-bottom: 0px!important;
}

.k-checkbox-label {
  font-size:  0.75rem!important;
  padding-top: 0px!important;
  padding-bottom: 0px!important;
  margin-top: -2px!important;
}

.k-form, .k-form-inline {
  font-size:  0.75!important;
}

.k-form {
  font-size:  0.75!important;
}

.k-column-title {
    font-weight: bolder!important;
}

.k-i-filter-clear {
    font-size: 0.9rem!important;
}

.nav-item {
  --bs-nav-link-padding-x: 0.4rem!important;
  --bs-nav-link-padding-y: 0.6rem!important;
}

.nav-link {
  font-size: 0.85rem!important;
}

.custom-datepicker {
        height: 50px;
        width: 100%;
}


    
.k-calendar .k-calendar-header .k-title.k-button {
        font-size: 6px!important;
}

button.accordion-button {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.col__spacing {
  padding-left: 10px!important;
  padding-right:20px!important;
  margin-top: 10px!important;
  //border-right: 1px solid grey;
 
}

</style>
